import {Box, ResponsiveContext, Text, ThemeContext} from "grommet";
import React, { useContext } from "react";
import { normalizeColor } from "grommet/utils";
import FontAwesomeIcon from "./FontAwesomeIcon";
import {TextBasedIcon, AccentBorderContainer} from "../utils/styled";
import ConditionalWrapper from "./ConditionalWrapper";
import { IconChevronRight as IconArrowRight } from '@tabler/icons-react';
import Button from "./Button";
import { snakeCase } from "../utils/string";

const MessageBanner = ({
    header,
    message,
    icon,
    color,
    ctaText,
    onClick,
    onDismiss,
    containerProps,
    textIcon,
    iconProps = {},
    ctaProps,
    iconWrapperProps={},
    headerProps = {},
    innerContainerProps = {},
    primaryCta,
    stacked = false, // When true, CTA appears below the content instead of on the right
    compact = false, // When true, renders a slimmer inline banner
}) => {
    const IconComponent = icon ? FontAwesomeIcon : TextBasedIcon;
    const size = useContext(ResponsiveContext);
    const isMobile = size === "small";
    // On a phone the arrow at the banner's end stays beside the text, so it never stacks
    const isStacked = stacked && !isMobile;
    return (
        <ThemeContext.Consumer>
            {(theme) => {
                const borderColor = normalizeColor(theme.global.colors.border, theme);
                const accentColor = normalizeColor(theme.global.colors[color], theme);
                return (
                    <ConditionalWrapper
                        wrapperIfTrue={(children) => (
                            <Button
                                event={{section: `${snakeCase(header || message)}_message_banner`, element: 'banner'}}
                                onClick={onClick}
                            >
                                {children}
                            </Button>
                        )}
                        wrapperIfFalse={(children) => (<>{children}</>)}
                        condition={isMobile && onClick}
                    >
                        <AccentBorderContainer
                            data-testid="message-banner"
                            compact={compact}
                            align={isStacked ? "start" : "center"}
                            gap="small"
                            justify="between"
                            // Grommet halves named padding on phones, which left 6px above and below the text
                            pad={compact ? { horizontal: 'small', vertical: 'xsmall' } : {vertical: isMobile ? '12px' : 'small', horizontal: 'xsmall'}}
                            elevation={compact ? "none" : "small"}
                            margin={compact ? 'none' : {vertical: isMobile ? 'small' : 'xsmall'}}
                            width={{max: '100%', min: 'small'}}
                            borderColor={borderColor}
                            accentColor={accentColor}
                            direction={isStacked ? "column" : "row"}
                            background="background"
                            {...containerProps}
                        >
                            <Box
                                align={isMobile ? "start" : "center"}
                                direction="row"
                                gap="small"
                                pad={compact ? 'none' : {left: 'small'}}
                                {...innerContainerProps}>
                                <Box width={{min: compact ? 'auto' : '24px'}} pad={isMobile && !compact ? {top: '2px'} : undefined} {...iconWrapperProps}>
                                    <IconComponent
                                        SelectedIcon={icon}
                                        text={textIcon}
                                        color={color}
                                        size={compact ? (isMobile ? "xsmall" : "small") : "medium"}
                                        {...iconProps} />
                                </Box>
                                <Box direction="column">
                                    {header && <Text size={compact ? "medium" : (isMobile ? "medium" : "large")} weight={compact ? 500 : 600} {...headerProps}>{header}</Text>}
                                    <Text size={compact ? "small" : undefined}>{message}</Text>
                                </Box>
                            </Box>
                            {/* Stacked, the container's 12px gap is all the space above the buttons,
                                matching the banner's 12px padding */}
                            {(ctaText || onDismiss) && !isMobile && (
                                <Box direction="row" gap="xxsmall" margin={isStacked ? {left: '48px'} : {horizontal: 'xsmall'}} flex={false} {...ctaProps}>
                                    {onDismiss && (
                                        <Button
                                            event={{section: `${snakeCase(header || message)}_message_banner`, element: 'dismiss'}}
                                            secondary
                                            label={<Text weight={400}>Dismiss</Text>}
                                            onClick={onDismiss}
                                        />
                                    )}
                                    {ctaText && (
                                        <Button
                                            event={{section: `${snakeCase(header || message)}_message_banner`, element: 'primary_cta'}}
                                            primary={!!primaryCta}
                                            secondary={!primaryCta}
                                            label={ctaText}
                                            onClick={onClick}
                                        />
                                    )}
                                </Box>
                            )}
                            {ctaText && isMobile && (
                                <Box pad={{right: 'small'}} flex={false}>
                                    <FontAwesomeIcon SelectedIcon={IconArrowRight} size="xxsmall" />
                                </Box>
                            )}
                        </AccentBorderContainer>
                    </ConditionalWrapper>
                );
            }}
        </ThemeContext.Consumer>
    );
}

export default MessageBanner;
